import React from "react";
import { useApp } from "../../../state/Context";
import Card from "../ui/Card";

export default function TermsAndConditions() {
  const { setSelection } = useApp();

  const LAST_UPDATED = new Date().toLocaleDateString();

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Back */}
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setSelection((prev) => ({ ...prev, step: "select" }))}
          className="
            rounded-xl border border-neutral-200 bg-white px-3 py-2
            text-sm font-semibold text-neutral-800
            hover:bg-neutral-100
            dark:border-neutral-800 dark:bg-neutral-950
            dark:text-neutral-100 dark:hover:bg-neutral-900
          "
        >
          ← Back to selection
        </button>
      </div>

      <Card className="p-6">
        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Terms &amp; Conditions</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Please read these terms carefully before using or purchasing Degree Classify UK. By
            accessing or using the service, you agree to be bound by these Terms.
          </p>
        </div>

        {/* Scroll panel */}
        <div className="mt-6">
          <div
            className="
              max-h-[70vh] overflow-y-auto rounded-xl border border-neutral-200 bg-white p-4
              dark:border-neutral-800 dark:bg-neutral-950
            "
          >
            {/* If you don’t use Tailwind Typography, replace `prose ...` with `space-y-4 text-sm` */}
            <div className="prose prose-neutral max-w-none text-sm dark:prose-invert">
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Last updated: {LAST_UPDATED}
              </p>

              <h2>1. Definitions</h2>
              <ul>
                <li>
                  <strong>“Service”</strong> means the Degree Classify UK website/app, calculators,
                  rulesets, features, and related content.
                </li>
                <li>
                  <strong>“We”, “Us”, “Our”</strong> means the operator of Degree Classify UK.
                </li>
                <li>
                  <strong>“You”</strong> means any user accessing or using the Service.
                </li>
                <li>
                  <strong>“Pro”</strong> means paid features (including any subscriptions or one-off
                  purchases) where offered.
                </li>
                <li>
                  <strong>“Content”</strong> means text, data, rules, calculations, UI, and other
                  materials made available through the Service.
                </li>
              </ul>
               <br/>
              <h2>2. Overview and acceptance</h2>
              <p>
                The Service provides an estimate of degree outcomes based on user-provided inputs
                and institution rulesets. It is not official academic advice, and must not be
                treated as such. By using the Service, you agree to these Terms and any additional
                terms that may apply to specific features.
              </p>
              <br/>
              <h2>3. Eligibility</h2>
              <p>
                You must be able to form a legally binding contract in your jurisdiction to use the
                Service. If you use the Service on behalf of an organisation, you confirm you have
                authority to bind that organisation to these Terms.
              </p>
               <br/>
              <h2>4. Accounts and access</h2>
              <ul>
                <li>
                  If the Service offers sign-in, you are responsible for maintaining the
                  confidentiality of your account and for all activity under it.
                </li>
                <li>
                  You must provide accurate information where requested and keep it up to date.
                </li>
                <li>
                  We may suspend or terminate access where we reasonably believe there is misuse,
                  fraud, or a security risk.
                </li>
              </ul>
                <br/>
              <h2>5. Licence and intellectual property</h2>
              <p>
                The Service and its Content are owned by us or our licensors and are protected by
                intellectual property laws. We grant you a limited, non-exclusive, non-transferable,
                revocable licence to access and use the Service for personal, lawful use in
                accordance with these Terms.
              </p>
              <ul>
                <li>You may not copy, modify, distribute, sell, or lease any part of the Service.</li>
                <li>
                  You may not reverse engineer or attempt to extract source code or rulesets,
                  except to the extent permitted by law.
                </li>
                <li>
                  You may not scrape, harvest, or systematically collect data from the Service
                  beyond normal personal use.
                </li>
              </ul>
                <br/>
              <h2>6. User inputs and responsibility</h2>
              <p>
                You are responsible for the accuracy and completeness of any grades, credits,
                module details, targets, and other inputs you enter or import. You must verify any
                outputs against your official records and your university’s published regulations.
              </p>
              <br/>
              <h2>7. Rulesets, coverage, and interpretation</h2>
              <p>
                Rulesets may be derived from published regulations and/or FOI material where
                available. Rulesets may be incomplete, may change, and may involve interpretation.
                Universities may apply discretionary decisions, borderline rules, scaling,
                reassessment policies, academic misconduct penalties, concessions, or regulatory
                adjustments not captured by the Service.
              </p>
              <br/>
              <h2>8. No guarantee / educational estimate</h2>
              <p>
                Outputs are indicative only and are not guaranteed. The Service does not guarantee
                that calculations match any university’s final award decision, and must not be
                relied upon as an official classification.
              </p>
               <br/>
              <h2>9. Pro features, pricing, and billing</h2>
              <p>
                Where paid functionality exists, access is provided as described at the time of
                purchase. Prices may change, but changes will not affect purchases already made.
              </p>
              <ul>
                <li>
                  If subscriptions are offered, they renew according to the interval shown at
                  checkout unless cancelled before renewal.
                </li>
                <li>
                  You are responsible for any taxes, fees, and charges imposed by your payment
                  provider.
                </li>
              </ul>
              <br/>
              <h2>10. Refunds and cancellation (general)</h2>
              <p>
                Refund eligibility depends on what you purchased and the applicable consumer laws
                in your jurisdiction. Unless required by law, payments are non-refundable once
                access is granted and/or the digital service is delivered.
              </p>
              <p>
                If you believe you were charged in error, contact support promptly with relevant
                details (date, receipt, and account email).
              </p>
              <br/>
              <h2>11. Acceptable use</h2>
              <p>You agree not to:</p>
              <ul>
                <li>Use the Service for unlawful, harmful, or fraudulent purposes.</li>
                <li>Attempt to gain unauthorised access to accounts, systems, or data.</li>
                <li>Interfere with or disrupt the Service, including via automated requests.</li>
                <li>
                  Upload or transmit malware, exploit code, or anything that compromises security.
                </li>
                <li>Misrepresent your identity or affiliation.</li>
              </ul>
                <br/>
              <h2>12. Availability and changes to the Service</h2>
              <p>
                We may modify, suspend, or discontinue any part of the Service at any time,
                including adding or removing features. We do not guarantee uninterrupted
                availability or error-free operation.
              </p>
              <br/>
              <h2>13. Third-party links and services</h2>
              <p>
                The Service may include links to third-party websites or services (e.g. payments,
                donation links, email, analytics). We are not responsible for third-party content,
                policies, or practices, and your use of third-party services is governed by their
                terms.
              </p>
              <br/>
              <h2>14. Privacy</h2>
              <p>
                Our Privacy page describes how we handle data. Where these Terms reference privacy,
                the Privacy page controls for data practices.
              </p>
              <br/>
              <h2>15. Disclaimers</h2>
              <p>
                The Service is provided “as is” and “as available”. To the fullest extent permitted
                by law, we disclaim all warranties, express or implied, including fitness for a
                particular purpose, accuracy, and non-infringement.
              </p>
                <br/>
              <h2>16. Limitation of liability</h2>
              <p>
                To the maximum extent permitted by law, we are not liable for any indirect,
                incidental, special, consequential, or punitive damages, or any loss of profits,
                data, goodwill, or reputation arising from or related to your use of the Service.
              </p>
              <p>
                Where liability cannot be excluded, our total liability for all claims relating to
                the Service is limited to the amount you paid us (if any) for the relevant paid
                feature in the 12 months before the claim arose.
              </p>
              <br/>
              <h2>17. Indemnity</h2>
              <p>
                You agree to indemnify and hold us harmless from any claims, damages, liabilities,
                costs, and expenses (including reasonable legal fees) arising from your misuse of
                the Service, your violation of these Terms, or your infringement of any rights of
                another party.
              </p>
              <br/>
              <h2>18. Termination</h2>
              <p>
                You may stop using the Service at any time. We may suspend or terminate access if
                you materially breach these Terms, if required by law, or to address security or
                operational risks. Sections that by their nature should survive will survive
                termination (including IP, disclaimers, limitation of liability, and indemnity).
              </p>
              <br/>
              <h2>19. Governing law and jurisdiction</h2>
              <p>
                These Terms are governed by the laws of the applicable jurisdiction for the Service
                operator. Any disputes will be subject to the exclusive jurisdiction of the courts
                in that jurisdiction, except where consumer protection laws require otherwise.
              </p>
              <br/>
              <h2>20. Changes to these Terms</h2>
              <p>
                We may update these Terms from time to time. If changes are material, we will take
                reasonable steps to make the updated Terms available. Continued use of the Service
                after updates constitutes acceptance of the updated Terms.
              </p>
              <br/>
              <h2>21. Contact</h2>
              <p>
                Questions? Email{" "}
                <a href="mailto:support@diceframe.co.uk">support@diceframe.co.uk</a>.
              </p>
              <br/>
              <hr />
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                <br/>
                If any provision of these Terms is found unenforceable, the remaining provisions
                remain in effect.
              </p>
            </div>
          </div>

          {/* Footer note (same vibe as HowItsDone disclaimer) */}
          <div className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400">
            These terms are provided for general use of the service and do not replace any official
            university regulations or legal advice. Always verify classification outcomes against
            your institution’s published regulations and official communications.
          </div>
        </div>
      </Card>
    </div>
  );
}